<script setup lang="ts">
import {computed, ref} from 'vue';
import {QForm} from 'quasar';
import {useEmailRule} from '@/ts/composables/pure/use-input-rules';
import {useSignIn, useSignOut} from '@/ts/composables/stateful/use-auth';
import {pagesPath} from '@/ts/router/pages-path';
import {OTHER_REGION_URL, ZITADEL} from '@/ts/utils/consts';
import {OTHER_REGION, looksLikeOtherRegion} from '@/ts/utils/region';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import WarningNote from '@/vue/atoms/warning-note.vue';
import OtherBalmNote from '@/vue/molecules/other-balm-note.vue';
import flagEu from '@/assets/img/flag-eu.svg';
import flagUs from '@/assets/img/flag-us.svg';

const email = defineModel<string>('email');

const props = defineProps<{
  externalError?: string;
  /**
   * The visitor authenticated with Google and stopped before the Balm account existed. Resolved by
   * the page, which asks the backend: a token alone would also match someone already signed in who
   * simply landed here (IPD-2844).
   */
  googleSignUpPending?: boolean;
  /** Why the last Google attempt ended without an account, when it did (IPD-2844). */
  notice?: string;
}>();

const emit = defineEmits<{
  finish: [],
  clearExternalError: [],
}>();

const form = ref<QForm>(null!);
const emailRule = useEmailRule();

// One switch for the whole feature: the environment's Zitadel org either has the Google provider
// (and its id here) or it does not. Without it the button would only lead to a sign-in screen with
// no Google on it, so it is not offered at all.
const isGoogleAvailable = Boolean(ZITADEL.google_idp_id);

const signIn = useSignIn();
const [goToLogin] = useLoadingNotification(async() => signIn());

// Each region runs its own Balm, and the domain someone signs up on fixes where their account lives
// (IPD-3376). Someone who landed on the other region's domain learns where theirs is here, and on
// every failed login on Zitadel's screen (IPD-3380). A pointer, not a redirect by location. The link
// carries no language: the other deployment picks one it serves, from the reader's browser.
// A reader whose device is set to the other region's time zone gets it on top, saying so (IPD-3543).
const otherBalm = computed(() => {
  if (!OTHER_REGION_URL) {
    return null;
  }
  const url = new URL(pagesPath.public.signUp, OTHER_REGION_URL);
  const located = looksLikeOtherRegion();
  // Named after the region the other Balm serves, which is the opposite of this deployment's.
  const region = OTHER_REGION.toLowerCase();
  return {
    href: url.href,
    host: url.host,
    located,
    keypath: located ? `other_balm_located_${region}` : `other_balm_hint_${region}`,
    flag: OTHER_REGION === 'US' ? flagUs : flagEu,
  };
});
const [goToGoogle] = useLoadingNotification(async() => signIn(null, {directToGoogle: true}));

// Registering by address is not a way out of that state: it collides with the identity Zitadel
// already holds, after the whole form. So while it lasts, Google is the way on and signing out is
// the way back.
const isGoogleSignUpPending = computed(() => isGoogleAvailable && Boolean(props.googleSignUpPending));

// Ending the Zitadel session too, not just ours: leaving it alive sends the next Google tap
// straight back into the registration they are trying to abandon. No backend logout on the way:
// the identity has no Balm account yet, which is the whole reason it is here.
const signOut = useSignOut();
const [useAnotherEmail] = useLoadingNotification(async() => {
  await signOut(pagesPath.public.signUp, {skipBeLogout: true});
});

async function submitForm(): Promise<void> {
  const isValid = await form.value.validate();
  if (!isValid) {
    return;
  }
  emit('finish');
}

// Clear the duplicate-email error and reset the field's validation as the user
// edits, so a prior error clears until the next Continue (lazy-rules: ondemand).
function onEmailInput(): void {
  emit('clearExternalError');
  form.value?.resetValidation();
}
</script>
<template>
  <div class="sign-up-email">
    <h1 class="title">
      {{ $t('sign_up') }}
    </h1>
    <warning-note v-if="notice" class="notice" :text="notice"/>
    <other-balm-note
      v-if="otherBalm?.located"
      class="other-balm-located"
      prominent
      :flag="otherBalm.flag"
      :keypath="otherBalm.keypath"
      :href="otherBalm.href"
      :host="otherBalm.host"
    />
    <q-form v-if="!isGoogleSignUpPending" ref="form" @submit.prevent="submitForm">
      <q-input
        v-model="email"
        :rules="[emailRule]"
        lazy-rules="ondemand"
        :error="Boolean(externalError)"
        :error-message="externalError"
        :label="$t('email')"
        autocomplete="email"
        type="text"
        inputmode="email"
        @update:model-value="onEmailInput"
      />
      <balm-btn
        class="full-btn"
        :label="$t('wizard_continue')"
        theme="blue"
        type="submit"
        :disable="!email?.trim()"
        icon="arrow-right.svg"
      />
    </q-form>

    <p v-if="isGoogleSignUpPending" class="pending-note">
      {{ $t('google_sign_up_pending') }}
    </p>

    <!--
      Lands on Google's account chooser directly, with no Zitadel screen in between (IPD-2844).
      The login link below goes to Zitadel's sign-in screen instead: same flow, different
      destination.
    -->
    <balm-btn
      v-if="isGoogleAvailable"
      class="full-btn"
      :label="$t('continue_with_google')"
      theme="grey"
      icon="google.svg"
      icon-position="left"
      @click="goToGoogle"
    />

    <q-btn
      v-if="isGoogleSignUpPending"
      flat
      :ripple="false"
      class="login-link"
      :label="$t('use_another_email')"
      @click="useAnotherEmail"
    />
    <q-btn
      v-else
      flat
      :ripple="false"
      class="login-link"
      :class="{'login-link--before-note': otherBalm && !otherBalm.located}"
      :label="$t('already_have_account')"
      @click="goToLogin"
    />

    <other-balm-note
      v-if="otherBalm && !otherBalm.located"
      class="other-balm-hint"
      :flag="otherBalm.flag"
      :keypath="otherBalm.keypath"
      :href="otherBalm.href"
      :host="otherBalm.host"
    />
  </div>
</template>
<style scoped lang="scss">
.title {
  @include font-display-4;
  margin-bottom: var(--Spacing-spacing-6);
}

// No container gap: it stacked on top of the title's own margin, giving this
// step more title-to-content spacing than the rest of the wizard.
.sign-up-email {
  display: flex;
  flex-direction: column;
}

.notice {
  margin-bottom: var(--Spacing-spacing-5);
}

// Explains why the form is not here, so the two remaining actions make sense.
.pending-note {
  margin: 0;
  color: var(--Neutral-UI-neutral-ui-60);

  @include font-body-sm;
}

.full-btn {
  width: 100%;
  margin-top: var(--Spacing-spacing-4);

  :deep(.balm-btn) {
    width: 100%;
  }
}



.login-link {
  @include focus-visible;
  align-self: center;
  margin-top: var(--Spacing-spacing-5);
  margin-bottom: var(--Spacing-spacing-7);
  text-decoration: underline;
  border-radius: var(--button-border-radius);

  // Plain text link per the design: the underline is the affordance, no
  // hover/press tint (keyboard focus stays via the focus-visible outline).
  :deep(.q-focus-helper) {
    display: none;
  }
}

.login-link--before-note {
  margin-bottom: var(--Spacing-spacing-4);
}

.other-balm-located {
  margin-bottom: var(--Spacing-spacing-5);
}

.other-balm-hint {
  margin-bottom: var(--Spacing-spacing-7);
}
</style>
